<script setup lang="ts">
import type { LeaderboardEntry, LeaderboardMetric } from '~/components/leaderboard/LeaderboardTable.vue'

const title = 'Leaderboard'
const description = 'The most popular pages on mychar.gg, ranked by views, clicks, clip views and setup clicks. See which creators are trending weekly, monthly and all-time.'

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

// OG image: snapshot of the all-time views top 5 at the moment the image is rendered
const { data: ogData } = await useAsyncData(
  'leaderboard-og',
  () => $fetch<{ entries: LeaderboardEntry[] }>('/api/leaderboard', {
    query: { period: 'all', sort: 'views' }
  }).catch(() => ({ entries: [] as LeaderboardEntry[] }))
)

function formatOgCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return count.toString()
}

defineOgImageComponent('Leaderboard', {
  entries: (ogData.value?.entries ?? []).slice(0, 5).map(e => ({
    rank: e.rank,
    name: e.displayName || e.username,
    username: e.username,
    avatarUrl: e.avatarUrl || '',
    views: formatOgCount(e.views)
  }))
})

const user = useSupabaseUser()
const isLoggedIn = computed(() => !!user.value?.sub)

// Default to all-time: the initial server render then links (almost) every
// page with any lifetime activity, which keeps profiles internally linked for
// crawlers (weekly/monthly boards are one click away and fetch client-side).
const period = ref<'weekly' | 'monthly' | 'all'>('all')
const metric = ref<LeaderboardMetric>('views')

const periodTabs = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'All-time', value: 'all' }
]

const metricTabs: { label: string, value: LeaderboardMetric, icon: string }[] = [
  { label: 'Views', value: 'views', icon: 'i-lucide-eye' },
  { label: 'Clicks', value: 'clicks', icon: 'i-lucide-mouse-pointer-click' },
  { label: 'Clip views', value: 'clipViews', icon: 'i-lucide-play' },
  { label: 'Setup clicks', value: 'setupClicks', icon: 'i-lucide-monitor' }
]

const SORT_PARAMS: Record<LeaderboardMetric, string> = {
  views: 'views',
  clicks: 'clicks',
  clipViews: 'clip_views',
  setupClicks: 'setup_clicks'
}

const { data, pending, error } = await useAsyncData(
  () => `leaderboard-${period.value}-${metric.value}`,
  () => $fetch<{ entries: LeaderboardEntry[] }>('/api/leaderboard', {
    query: { period: period.value, sort: SORT_PARAMS[metric.value] }
  }),
  { watch: [period, metric] }
)

const entries = computed(() => data.value?.entries ?? [])
</script>

<template>
  <div class="min-h-screen">
    <UContainer class="pb-24 pt-28 sm:pt-32">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-highlighted sm:text-4xl">
          Leaderboard
        </h1>
        <p class="mt-2 text-muted">
          The most popular pages on mychar right now.
        </p>
      </div>

      <!-- Filters -->
      <div class="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            v-for="m in metricTabs"
            :key="m.value"
            class="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors duration-200"
            :class="metric === m.value
              ? 'border-accented bg-elevated text-highlighted'
              : 'border-transparent text-muted hover:text-highlighted'"
            @click="metric = m.value"
          >
            <UIcon
              :name="m.icon"
              class="size-4"
            />
            {{ m.label }}
          </button>
        </div>

        <UTabs
          v-model="period"
          :items="periodTabs"
          :content="false"
          size="sm"
        />
      </div>

      <!-- Board -->
      <LeaderboardTable
        :entries="entries"
        :loading="pending"
        :error="!!error"
        :metric="metric"
      />

      <!-- CTA for logged-out visitors -->
      <div
        v-if="!isLoggedIn"
        class="relative mt-8 overflow-hidden rounded-3xl border border-primary-400/15 bg-primary-500/4 p-8 text-center shadow-(--surface-shadow)"
      >
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -left-20 -top-20 size-56 rounded-full bg-primary-500/10 blur-3xl"
        />
        <div class="relative flex flex-col items-center gap-4">
          <p class="text-lg font-semibold text-highlighted">
            Want your page on this board?
          </p>
          <div class="flex w-full max-w-lg justify-center">
            <HeroClaimUsername variant="compact" />
          </div>
        </div>
      </div>
    </UContainer>
  </div>
</template>
